import type {
  TenantTimezoneConfig,
  TimezoneEngineConfig,
} from "@multisystemsuite/timezone-engine-shared-types";
import { createTimezoneEngine, type TimezoneEngine } from "./engine.js";
import { safeTimezoneFallback } from "./validation.js";

/** Multi-tenant timezone registry for SaaS / HRMS / ERP isolation */
export class TenantTimezoneRegistry {
  private readonly tenants = new Map<string, TenantTimezoneConfig>();
  private readonly engines = new Map<string, TimezoneEngine>();

  register(config: TenantTimezoneConfig): void {
    const timezone = safeTimezoneFallback(config.timezone);
    this.tenants.set(config.tenantId, { ...config, timezone });
    this.engines.delete(config.tenantId);
  }

  unregister(tenantId: string): void {
    this.tenants.delete(tenantId);
    this.engines.delete(tenantId);
  }

  getConfig(tenantId: string): TenantTimezoneConfig | undefined {
    return this.tenants.get(tenantId);
  }

  getTimezone(tenantId: string, fallback = "UTC"): string {
    return this.tenants.get(tenantId)?.timezone ?? fallback;
  }

  getLocale(tenantId: string, fallback = "en-US"): string {
    return this.tenants.get(tenantId)?.locale ?? fallback;
  }

  /** Create or retrieve an isolated engine instance per tenant */
  getEngine(tenantId: string, config: TimezoneEngineConfig = {}): TimezoneEngine {
    const existing = this.engines.get(tenantId);
    if (existing) return existing;

    const tenant = this.tenants.get(tenantId);
    const engineConfig: TimezoneEngineConfig = {
      defaultTimezone: tenant?.timezone ?? "UTC",
      defaultLocale: tenant?.locale ?? "en-US",
      ...config,
    };
    if (tenant?.defaultFormat) {
      engineConfig.defaultFormat = tenant.defaultFormat;
    }
    const engine = createTimezoneEngine(engineConfig);
    this.engines.set(tenantId, engine);
    return engine;
  }

  listTenants(): string[] {
    return [...this.tenants.keys()];
  }
}

/** Create a tenant-scoped timezone engine with isolated configuration */
export function createTenantTimezoneEngine(
  tenantId: string,
  timezone: string,
  config: TimezoneEngineConfig = {},
): TimezoneEngine {
  return createTimezoneEngine({
    ...config,
    defaultTimezone: safeTimezoneFallback(timezone),
  });
}

/** Default singleton tenant registry */
export const defaultTenantRegistry = new TenantTimezoneRegistry();
